import { z } from "zod";
import { OutputAssetSchema } from "./assets";
import { ExternalMappingSchema } from "./externalMappings";

// ============================================================================
// NOTE ENTITY TYPE — which entities can have notes
// ============================================================================

export const NOTE_ENTITY_TYPE_VALUES = [
  "ITEM",
  "QUOTE",
  "OPPORTUNITY",
  "PERSON",
  "COMPANY",
] as const;
export const NoteEntityTypeSchema = z.enum(NOTE_ENTITY_TYPE_VALUES);
export type NoteEntityType = z.infer<typeof NoteEntityTypeSchema>;
export const NOTE_ENTITY_TYPE = NoteEntityTypeSchema.enum;

// ============================================================================
// NOTE ACTOR TYPE — who created the note
// ============================================================================

/** ERP_SYNC notes are written by an ERP pull; their actorId is the ERP provider key. */
export const NOTE_ACTOR_TYPE_VALUES = [
  "USER",
  "AGENT_WORKFLOW_RUN",
  "ERP_SYNC",
] as const;
export const NoteActorTypeSchema = z.enum(NOTE_ACTOR_TYPE_VALUES);
export type NoteActorType = z.infer<typeof NoteActorTypeSchema>;
export const NOTE_ACTOR_TYPE = NoteActorTypeSchema.enum;

// ============================================================================
// NOTE SCHEMAS
// ============================================================================

/** Schema for a note response object */
export const NoteSchema = z.object({
  id: z.string(),
  entityType: NoteEntityTypeSchema,
  entityId: z.string(),
  content: z.string(),
  actorId: z.string().nullable(),
  actorType: NoteActorTypeSchema,
  actorName: z.string().nullable(),
  userId: z.string().nullable(),
  organizationId: z.string(),
  assetIds: z.array(z.string()),
  assets: z.array(OutputAssetSchema).optional(),
  externalMappings: z.array(ExternalMappingSchema).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Note = z.infer<typeof NoteSchema>;

/** Schema for creating a note */
export const CreateNoteParamsSchema = z.object({
  entityType: NoteEntityTypeSchema,
  entityId: z.string().min(1),
  content: z.string().min(1),
  assetIds: z.array(z.string()).optional(),
});
export type CreateNoteParams = z.infer<typeof CreateNoteParamsSchema>;

/** Schema for updating a note */
export const UpdateNoteParamsSchema = z.object({
  content: z.string().min(1),
  assetIds: z.array(z.string()).optional(),
});
export type UpdateNoteParams = z.infer<typeof UpdateNoteParamsSchema>;

/** Schema for list notes API response */
export const ListNotesResponseSchema = z.object({
  notes: z.array(NoteSchema),
});
export type ListNotesResponse = z.infer<typeof ListNotesResponseSchema>;

/** Schema for single note API response */
export const NoteResponseSchema = z.object({
  note: NoteSchema,
});
export type NoteResponse = z.infer<typeof NoteResponseSchema>;
